import {useCallback, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {filterNullFields} from 'schemas/fields/filterNullFields';
import removeNulls from 'util/removeNulls';

import {DEFAULT_OUTPUT_SCHEMA_PREFIX} from './ProcessTaskSchemaEditorConstants';
import type {MinimalProcessTaskSchema} from './ProcessTaskSchemaUtils';
import type {UpdateProcessSchema} from './ProcessTaskSchemaUtils';

export interface SchemaFieldsState {
  hasOutputSchema: boolean;
  taskFields: Array<ClientSchemaField>;
  outputFields: Array<ClientSchemaField>;
  outputSchemaPrefix: string;
  autopopulateDirectOutputsFromTasks: boolean;
  validRelevantForms: MinimalProcessTaskSchema['relevantForms'];
  taskFieldTableKey: string;
  outputFieldTableKey: string;
  flowchartConfigTableKey: string;

  setHasOutputSchema: React.Dispatch<React.SetStateAction<boolean>>;
  setTaskFields: React.Dispatch<React.SetStateAction<Array<ClientSchemaField>>>;
  setOutputFields: React.Dispatch<React.SetStateAction<Array<ClientSchemaField>>>;
  setOutputSchemaPrefix: React.Dispatch<React.SetStateAction<string>>;
  setAutopopulateDirectOutputsFromTasks: React.Dispatch<React.SetStateAction<boolean>>;
  setValidRelevantForms: React.Dispatch<React.SetStateAction<MinimalProcessTaskSchema['relevantForms']>>;

  applyFieldUpdates: (params: UpdateProcessSchema & {updateTables?: boolean}) => void;
}

export function useSchemaFieldsState(processTaskSchema: MinimalProcessTaskSchema | null): SchemaFieldsState {
  const [hasOutputSchema, setHasOutputSchema] = useState(
    processTaskSchema == null || processTaskSchema.outputSchema != null
  );
  const [taskFields, _setTaskFields] = useState<Array<ClientSchemaField>>(
    filterNullFields(processTaskSchema?.allFields)
  );
  const setTaskFields = useCallback((update: React.SetStateAction<Array<ClientSchemaField>>) => {
    _setTaskFields((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;
      return removeNulls(next);
    });
  }, []);
  const [outputFields, _setOutputFields] = useState<Array<ClientSchemaField>>(
    filterNullFields(processTaskSchema?.outputSchema?.allFields)
  );
  const setOutputFields = useCallback((update: React.SetStateAction<Array<ClientSchemaField>>) => {
    _setOutputFields((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;
      return removeNulls(next);
    });
  }, []);
  const [outputSchemaPrefix, setOutputSchemaPrefix] = useState(
    processTaskSchema?.outputSchema?.prefix ?? DEFAULT_OUTPUT_SCHEMA_PREFIX
  );
  const [autopopulateDirectOutputsFromTasks, setAutopopulateDirectOutputsFromTasks] = useState(
    Boolean(processTaskSchema?.outputSchema?.directExecutionModalAutopopulateTaskIds)
  );
  const [validRelevantForms, setValidRelevantForms] = useState<MinimalProcessTaskSchema['relevantForms']>(
    processTaskSchema?.relevantForms?.filter(
      (formOrInaccessible) =>
        isInaccessibleInfo(formOrInaccessible) || (formOrInaccessible as {isValid: boolean}).isValid
    ) || []
  );

  const [taskFieldTableKey, setTaskFieldTableKey] = useState(uuidv4());
  const [outputFieldTableKey, setOutputFieldTableKey] = useState(uuidv4());
  const [flowchartConfigTableKey, setFlowchartConfigTableKey] = useState(uuidv4());

  const applyFieldUpdates = useCallback(
    (params: UpdateProcessSchema & {updateTables?: boolean}): void => {
      if (params.taskFields != null) {
        setTaskFields(params.taskFields);
        setValidRelevantForms([]);
        if (params.updateTables) {
          setTaskFieldTableKey(uuidv4());
        }
      }
      if (params.outputSchemaPrefix != null) {
        setOutputSchemaPrefix(params.outputSchemaPrefix);
      }
      if (params.outputFields != null) {
        setOutputFields(params.outputFields);
        if (params.updateTables) {
          setOutputFieldTableKey(uuidv4());
        }
      }
      if (params.autopopulateDirectOutputsFromTasks != null) {
        setAutopopulateDirectOutputsFromTasks(params.autopopulateDirectOutputsFromTasks);
      }
      if (params.flowchartConfig !== undefined && params.updateTables) {
        setFlowchartConfigTableKey(uuidv4());
      }
    },
    [setTaskFields, setOutputFields]
  );

  return {
    hasOutputSchema,
    taskFields,
    outputFields,
    outputSchemaPrefix,
    autopopulateDirectOutputsFromTasks,
    validRelevantForms,
    taskFieldTableKey,
    outputFieldTableKey,
    flowchartConfigTableKey,

    setHasOutputSchema,
    setTaskFields,
    setOutputFields,
    setOutputSchemaPrefix,
    setAutopopulateDirectOutputsFromTasks,
    setValidRelevantForms,

    applyFieldUpdates,
  };
}
